import _ from 'lodash';

/**
 * Reload and reapply — what a settings form does with a 409 `stale_collection`
 * or `stale_card` (delete audit F6): somebody saved the record since this
 * form loaded it, the API refused the save and handed back the record as it
 * stands, and the person's own edits go on top of THAT rather than over it.
 *
 * Three copies are compared, top-level key by key:
 *
 *   base    the record as the form loaded it (the last save's answer)
 *   mine    the form as it stands
 *   theirs  the record as it stands now (the 409's payload)
 *
 * A key I did not change takes theirs. A key I changed takes mine — except
 * the keys named in `lists` and `objects`, which merge one level down so a
 * colleague's new card member or new element is not dropped by my edit to
 * another one:
 *
 *   lists    { key: entry => id } — an array merged by entry id: my order,
 *            my additions, my edits and my removals; their additions
 *            appended; an entry I left alone takes theirs (or goes, if they
 *            removed it).
 *   objects  [key] — an object merged by property, the same rule.
 *
 * `ignore` keys (ids, lifecycle stamps, response-only annotations) always
 * take theirs. Returns `{ merged, mine: [keys I changed], overlap: [keys
 * we both changed, to different values] }`.
 */
export function reapplyOver({ base = {}, mine = {}, theirs = {}, ignore = [], lists = {}, objects = [] }) {
	const skip = new Set(ignore);
	const keys = new Set([...Object.keys(base || {}), ...Object.keys(mine || {})]);
	const merged = { ...(theirs || {}) };
	const changedByMe = [];
	const overlap = [];
	keys.forEach(key => {
		if (skip.has(key)) return;
		const b = base?.[key];
		const m = mine?.[key];
		const t = theirs?.[key];
		if (_.isEqual(m, b)) return;
		changedByMe.push(key);
		if (!_.isEqual(t, b) && !_.isEqual(t, m)) overlap.push(key);
		if (lists[key] && Array.isArray(m)) {
			merged[key] = mergeList(Array.isArray(b) ? b : [], m, Array.isArray(t) ? t : [], lists[key]);
			return;
		}
		if (objects.includes(key) && isPlainObject(m)) {
			merged[key] = mergeObject(isPlainObject(b) ? b : {}, m, isPlainObject(t) ? t : {});
			return;
		}
		if (m === undefined) delete merged[key];
		else merged[key] = m;
	});
	return { merged, mine: changedByMe, overlap };
}

/** Only the top-level keys of `mine` that differ from `base`, `ignore` aside. */
export function changedKeys(base = {}, mine = {}, ignore = []) {
	const skip = new Set(ignore);
	const keys = new Set([...Object.keys(base || {}), ...Object.keys(mine || {})]);
	return [...keys].filter(key => !skip.has(key) && !_.isEqual(mine?.[key], base?.[key]));
}

const isPlainObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function mergeObject(base, mine, theirs) {
	const out = { ...theirs };
	new Set([...Object.keys(base), ...Object.keys(mine)]).forEach(key => {
		if (_.isEqual(mine[key], base[key])) return;
		if (mine[key] === undefined) delete out[key];
		else out[key] = mine[key];
	});
	return out;
}

export function mergeList(base, mine, theirs, keyOf) {
	const idOf = entry => {
		try { return keyOf(entry) || null; } catch { return null; }
	};
	const byId = list => new Map(list.map(entry => [idOf(entry), entry]).filter(([id]) => id));
	const baseById = byId(base);
	const theirsById = byId(theirs);
	const mineIds = new Set(mine.map(idOf).filter(Boolean));
	const out = [];
	mine.forEach(entry => {
		const id = idOf(entry);
		const was = id ? baseById.get(id) : undefined;
		// Mine and new (or not addressable): kept as I have it.
		if (!id || was === undefined) { out.push(entry); return; }
		// Mine and edited: kept as I have it.
		if (!_.isEqual(entry, was)) { out.push(entry); return; }
		// Untouched by me: theirs, or gone if they removed it.
		if (theirsById.has(id)) out.push(theirsById.get(id));
	});
	// Theirs and new since the form loaded: appended.
	theirs.forEach(entry => {
		const id = idOf(entry);
		if (!id || mineIds.has(id) || baseById.has(id)) return;
		out.push(entry);
	});
	return out;
}
